/**
 * Razorpay Standard Checkout helper.
 * Lazily loads checkout.js, calls our /api/razorpay/create-order,
 * opens the modal, then verifies the signature via /api/razorpay/verify-payment.
 */

const CHECKOUT_SRC = "https://checkout.razorpay.com/v1/checkout.js";
const API = process.env.REACT_APP_BACKEND_URL;
const KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

let scriptPromise = null;

function loadScript() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.Razorpay) return Promise.resolve(true);
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = CHECKOUT_SRC;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load Razorpay checkout script"));
    };
    document.body.appendChild(s);
  });
  return scriptPromise;
}

/**
 * Open the Razorpay modal for the given amount + prefill.
 * @param {object} args
 * @param {number} args.amount        Amount in the smallest currency unit (paise/cents).
 * @param {string} [args.currency]    Default INR.
 * @param {string} [args.name]        Merchant display name (top of modal).
 * @param {string} [args.description] Description shown under merchant name.
 * @param {object} [args.prefill]     { name, email, contact }
 * @param {object} [args.notes]       Optional metadata to attach.
 * @param {string} [args.image]       Merchant logo URL.
 * @param {string} [args.theme_color] Modal accent color (hex).
 * @returns {Promise<{verified:true, order_id:string, payment_id:string}>}
 */
export async function openRazorpayCheckout(args) {
  const {
    amount,
    currency = "INR",
    name = "Aeonyx Life Sciences",
    description = "CryoVault checkout",
    prefill = {},
    notes = {},
    image,
    theme_color = "#01CFC6",
  } = args || {};

  if (!KEY_ID) throw new Error("REACT_APP_RAZORPAY_KEY_ID is not set");
  if (!API) throw new Error("REACT_APP_BACKEND_URL is not set");
  if (!Number.isFinite(amount) || amount < 100) {
    throw new Error("Amount must be at least 100 (smallest currency unit)");
  }

  await loadScript();

  // 1. Create the order on our backend
  const orderRes = await fetch(`${API}/api/razorpay/create-order`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ amount, currency, notes }),
  });
  if (!orderRes.ok) {
    const err = await orderRes.text().catch(() => "");
    throw new Error(`Failed to create order: ${err || orderRes.status}`);
  }
  const order = await orderRes.json();

  // 2. Open the Razorpay modal
  return new Promise((resolve, reject) => {
    const options = {
      key: order.key_id || KEY_ID,
      amount: order.amount,
      currency: order.currency,
      order_id: order.order_id,
      name,
      description,
      image,
      prefill,
      notes,
      theme: { color: theme_color },
      handler: async (response) => {
        try {
          const verifyRes = await fetch(`${API}/api/razorpay/verify-payment`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          if (!verifyRes.ok) {
            const err = await verifyRes.text().catch(() => "");
            reject(new Error(`Signature verification failed: ${err || verifyRes.status}`));
            return;
          }
          const data = await verifyRes.json();
          resolve(data);
        } catch (e) {
          reject(e);
        }
      },
      modal: {
        ondismiss: () => {
          reject(new Error("Payment cancelled"));
        },
      },
    };

    const rp = new window.Razorpay(options);
    rp.on("payment.failed", (resp) => {
      const desc = resp?.error?.description || "Payment failed";
      reject(new Error(desc));
    });
    rp.open();
  });
}
